import './episode-table.scss';
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import React from 'react';
import { Column, TriggerTypeEnum } from '@models/table.model.ts';
import If from '@components/if/if.tsx';
import Loader from '@components/loader/loader.tsx';
import { ICONS } from '@components/icons/icon.tsx';
import { useTranslation } from 'react-i18next';
import setUpForMeIcon from '@assets/icons/setup-for-me.svg';
import emptyIcon from '@assets/icons/empty.svg';

const EpisodeTable: React.FC<{
  onTriggerAction?: (type: TriggerTypeEnum) => void;
  setupForMe?: boolean;
  setupLoading?: boolean;
  columns: Column[];
  tableData: any[];
  isLoading?: boolean;
  isLoaded?: boolean;
}> = ({
  columns,
  tableData,
  onTriggerAction,
  setupForMe,
  setupLoading,
  isLoading,
  isLoaded
}) => {
  const { t } = useTranslation();

  const showEmpty = tableData?.length === 0 && !setupForMe;
  const showSetup = tableData?.length === 0 && setupForMe;

  return (
    <div className="w-full h-full ml-auto mr-auto">
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.id}>
                    <div className="w-full flex items-center justify-between text-nowrap">
                      <span>{col.label}</span>
                    </div>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <If
                loading={isLoading}
                condition={isLoaded && tableData?.length > 0}
                loadingContent={
                  <TableRow>
                    <TableCell
                      colSpan={columns.length + 1}
                      className="text-center p-4"
                    >
                      <div className="flex justify-center items-center pt-[14px]">
                        <Loader />
                      </div>
                    </TableCell>
                  </TableRow>
                }
                emptyContent={
                  <TableRow>
                    <TableCell colSpan={columns.length + 1}>
                      {showSetup && (
                        <div className="flex flex-col justify-center items-center py-9 gap-6 max-lg-xl:gap-5">
                          <img
                            src={setUpForMeIcon}
                            alt="Setup"
                            className="max-lg-xl:w-[110px] max-h-[300px]"
                          />
                          <p className="text-[var(--blue-4)] text-[30px] opacity-40 font-semibold text-center max-lg-xl:text-[15px]">
                            {t('setup_for_me_content_text')}
                          </p>
                          <Button
                            className="!px-5"
                            variant="contained"
                            color="primary"
                            startIcon={ICONS.plusIcon}
                            loading={setupLoading}
                            onClick={() =>
                              onTriggerAction?.(TriggerTypeEnum.Setup)
                            }
                          >
                            {t('setup_for_me_button_text')}
                          </Button>
                        </div>
                      )}
                      {showEmpty && (
                        <div className="flex flex-col justify-center items-center py-9 gap-6 max-lg-xl:gap-5">
                          <img
                            src={emptyIcon}
                            alt="Empty"
                            className="max-lg-xl:w-[110px] max-h-[300px]"
                          />
                          <p className="text-[var(--blue-4)] text-[30px] opacity-40 font-semibold text-center max-lg-xl:text-[15px]">
                            {t('empty_content_text')}
                          </p>
                          <Button
                            className="!px-5"
                            variant="contained"
                            color="primary"
                            startIcon={ICONS.plusIcon}
                            onClick={() =>
                              onTriggerAction?.(TriggerTypeEnum.Add)
                            }
                          >
                            {t('empty_content_button_text')}
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                }
              >
                {tableData?.map((row, rowIndex) => (
                  <TableRow key={row?.id || rowIndex}>
                    {columns?.map((column) => (
                      <TableCell key={column.id}>
                        <div className="clamp-text">
                          {column?.render(row[column?.field], row)}
                        </div>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </If>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
};

export default EpisodeTable;
